/**
 * FOVCalculator - Helper module for Field of View calculations
 * Separates mathematical logic from the FOVIndicator model for better testability and reusability
 */
const FOVCalculator = {
  // Mathematical constants
  RAD_TO_DEG: 180 / Math.PI,
  MICRONS_TO_MM: 1000,
  DEFAULT_FOCAL_LENGTH_ADJUST: 1,

  // Default FOV structure
  DEFAULT_FOV: {
    width: 0.0,
    height: 0.0,
    offsetX: 0.0,
    offsetY: 0.0,
  },

  // Error values
  ERROR_FOV: {
    width: -1,
    height: -1,
    offsetX: -1,
    offsetY: -1,
  },

  // Validation constants
  VALIDATION: {
    MAX_FOV_DEGREES: 360,
    MIN_FOV_DEGREES: 0.000001,
    MAX_FOCAL_LENGTH: 50000, // 50 meters in mm
    MIN_FOCAL_LENGTH: 1, // 1mm minimum
    MAX_MAGNIFICATION: 10000,
    MIN_MAGNIFICATION: 0.1,
    MAX_SENSOR_SIZE: 1000, // 1 meter in mm
    MIN_SENSOR_SIZE: 0.001, // 1 micrometer in mm
  },

  // Error messages
  ERROR_MESSAGES: {
    INVALID_EQUIPMENT: "Invalid equipment object provided",
    MISSING_PROPERTIES: "Required properties missing from equipment",
    CALCULATION_ERROR: "Error during FOV calculation",
    INVALID_PARAMETERS: "Invalid parameters provided",
    OUT_OF_RANGE: "Value outside acceptable range",
  },

  /**
   * Calculates focal length adjustment factor from barlow/reducer equipment
   * @param {Object} barlowReducer - The barlow/reducer equipment object
   * @returns {number} Focal length adjustment factor (default: 1)
   */
  calculateFocalLengthAdjustment: function (barlowReducer) {
    try {
      if (!barlowReducer) {
        return this.DEFAULT_FOCAL_LENGTH_ADJUST;
      }

      if (typeof barlowReducer.get !== "function") {
        console.warn("FOVCalculator: Invalid barlow/reducer object - missing get method");
        return this.DEFAULT_FOCAL_LENGTH_ADJUST;
      }

      const properties = barlowReducer.get("properties");
      if (!properties || typeof properties !== "object") {
        return this.DEFAULT_FOCAL_LENGTH_ADJUST;
      }

      const magnification = parseFloat(properties.magnification);
      if (Number.isNaN(magnification) || magnification <= 0) {
        console.warn("FOVCalculator: Invalid magnification value:", properties.magnification);
        return this.DEFAULT_FOCAL_LENGTH_ADJUST;
      }

      if (magnification > this.VALIDATION.MAX_MAGNIFICATION) {
        console.warn("FOVCalculator: Magnification exceeds maximum:", magnification);
        return this.VALIDATION.MAX_MAGNIFICATION;
      }

      return magnification;
    } catch (error) {
      console.error("FOVCalculator: Error calculating focal length adjustment:", error);
      return this.DEFAULT_FOCAL_LENGTH_ADJUST;
    }
  },

  /**
   * Calculates magnification for telescope + eyepiece combination
   * @param {Object} telescope - Telescope equipment object
   * @param {Object} eyepiece - Eyepiece equipment object
   * @param {number} focalLengthAdjust - Focal length adjustment factor
   * @returns {number} Magnification value
   */
  calculateMagnification: function (telescope, eyepiece, focalLengthAdjust = 1) {
    try {
      // Validate input objects
      if (!telescope || typeof telescope.get !== "function") {
        console.warn("FOVCalculator: Invalid telescope object for magnification calculation");
        return 0;
      }

      if (!eyepiece || typeof eyepiece.get !== "function") {
        console.warn("FOVCalculator: Invalid eyepiece object for magnification calculation");
        return 0;
      }

      // Validate telescope properties
      const telescopeProps = telescope.get("properties");
      if (!telescopeProps || !telescopeProps.focalLength) {
        console.warn("FOVCalculator: Telescope missing focal length property");
        return 0;
      }

      // Validate eyepiece properties
      const eyepieceProps = eyepiece.get("properties");
      if (!eyepieceProps || !eyepieceProps.focalLength) {
        console.warn("FOVCalculator: Eyepiece missing focal length property");
        return 0;
      }

      const telescopeFocalLength = parseFloat(telescopeProps.focalLength);
      const eyepieceFocalLength = parseFloat(eyepieceProps.focalLength);

      // Validate numeric values
      if (Number.isNaN(telescopeFocalLength) || telescopeFocalLength <= 0) {
        console.warn("FOVCalculator: Invalid telescope focal length:", telescopeProps.focalLength);
        return 0;
      }

      if (Number.isNaN(eyepieceFocalLength) || eyepieceFocalLength <= 0) {
        console.warn("FOVCalculator: Invalid eyepiece focal length:", eyepieceProps.focalLength);
        return 0;
      }

      if (Number.isNaN(focalLengthAdjust) || focalLengthAdjust <= 0) {
        console.warn("FOVCalculator: Invalid focal length adjustment:", focalLengthAdjust);
        focalLengthAdjust = 1;
      }

      // Validate ranges
      if (telescopeFocalLength > this.VALIDATION.MAX_FOCAL_LENGTH) {
        console.warn(
          "FOVCalculator: Telescope focal length exceeds maximum:",
          telescopeFocalLength,
        );
      }

      if (eyepieceFocalLength > this.VALIDATION.MAX_FOCAL_LENGTH) {
        console.warn("FOVCalculator: Eyepiece focal length exceeds maximum:", eyepieceFocalLength);
      }

      const magnification = (telescopeFocalLength * focalLengthAdjust) / eyepieceFocalLength;

      // Validate result
      if (magnification > this.VALIDATION.MAX_MAGNIFICATION) {
        console.warn("FOVCalculator: Calculated magnification exceeds maximum:", magnification);
      }

      return magnification;
    } catch (error) {
      console.error("FOVCalculator: Error calculating magnification:", error);
      return 0;
    }
  },

  /**
   * Calculates FOV for telescope + eyepiece combination
   * @param {Object} telescope - Telescope equipment object
   * @param {Object} eyepiece - Eyepiece equipment object
   * @param {number} focalLengthAdjust - Focal length adjustment factor
   * @returns {Object} FOV object with width calculated
   */
  calculateTelescopeEyepieceFOV: function (telescope, eyepiece, focalLengthAdjust = 1) {
    try {
      const fov = { ...this.DEFAULT_FOV };

      if (!eyepiece || typeof eyepiece.get !== "function") {
        console.warn("FOVCalculator: Invalid eyepiece object for FOV calculation");
        return fov;
      }

      const properties = eyepiece.get("properties");
      if (!properties || !properties.apparentFOV) {
        console.warn("FOVCalculator: Eyepiece missing apparent FOV property");
        return fov;
      }

      const apparentFOV = parseFloat(properties.apparentFOV);
      if (Number.isNaN(apparentFOV) || apparentFOV <= 0) {
        console.warn("FOVCalculator: Invalid apparent FOV value:", properties.apparentFOV);
        return fov;
      }

      if (apparentFOV > this.VALIDATION.MAX_FOV_DEGREES) {
        console.warn("FOVCalculator: Apparent FOV exceeds maximum:", apparentFOV);
      }

      const magnification = this.calculateMagnification(telescope, eyepiece, focalLengthAdjust);
      if (magnification > 0) {
        fov.width = apparentFOV / magnification;

        // Validate calculated FOV
        if (fov.width > this.VALIDATION.MAX_FOV_DEGREES) {
          console.warn("FOVCalculator: Calculated FOV exceeds maximum degrees:", fov.width);
        }
      }

      return fov;
    } catch (error) {
      console.error("FOVCalculator: Error calculating telescope+eyepiece FOV:", error);
      return { ...this.DEFAULT_FOV };
    }
  },

  /**
   * Converts camera sensor dimensions from pixels to millimeters
   * @param {Object} cameraProps - Camera properties object
   * @returns {Object} Camera dimensions in mm or null if invalid
   */
  convertCameraDimensionsToMM: function (cameraProps) {
    try {
      if (!cameraProps || typeof cameraProps !== "object") {
        console.warn("FOVCalculator: Invalid camera properties object");
        return null;
      }

      // Check for required properties
      const requiredProps = [
        "sensorWidthInPixels",
        "sensorHeightInPixels",
        "sensorPixelWidth",
        "sensorPixelHeight",
      ];
      for (const prop of requiredProps) {
        if (cameraProps[prop] === undefined || cameraProps[prop] === null) {
          console.warn(`FOVCalculator: Camera missing required property: ${prop}`);
          return null;
        }
      }

      // Parse and validate values
      const widthPixels = parseFloat(cameraProps.sensorWidthInPixels);
      const heightPixels = parseFloat(cameraProps.sensorHeightInPixels);
      const pixelWidth = parseFloat(cameraProps.sensorPixelWidth);
      const pixelHeight = parseFloat(cameraProps.sensorPixelHeight);

      // Validate numeric values
      if (Number.isNaN(widthPixels) || widthPixels <= 0) {
        console.warn(
          "FOVCalculator: Invalid sensor width in pixels:",
          cameraProps.sensorWidthInPixels,
        );
        return null;
      }

      if (Number.isNaN(heightPixels) || heightPixels <= 0) {
        console.warn(
          "FOVCalculator: Invalid sensor height in pixels:",
          cameraProps.sensorHeightInPixels,
        );
        return null;
      }

      if (Number.isNaN(pixelWidth) || pixelWidth <= 0) {
        console.warn("FOVCalculator: Invalid pixel width:", cameraProps.sensorPixelWidth);
        return null;
      }

      if (Number.isNaN(pixelHeight) || pixelHeight <= 0) {
        console.warn("FOVCalculator: Invalid pixel height:", cameraProps.sensorPixelHeight);
        return null;
      }

      // Calculate dimensions in mm
      const widthMM = (widthPixels * pixelWidth) / this.MICRONS_TO_MM;
      const heightMM = (heightPixels * pixelHeight) / this.MICRONS_TO_MM;

      // Validate reasonable sensor sizes
      if (widthMM > this.VALIDATION.MAX_SENSOR_SIZE || widthMM < this.VALIDATION.MIN_SENSOR_SIZE) {
        console.warn("FOVCalculator: Sensor width outside reasonable range (mm):", widthMM);
      }

      if (
        heightMM > this.VALIDATION.MAX_SENSOR_SIZE ||
        heightMM < this.VALIDATION.MIN_SENSOR_SIZE
      ) {
        console.warn("FOVCalculator: Sensor height outside reasonable range (mm):", heightMM);
      }

      return {
        widthMM: widthMM,
        heightMM: heightMM,
      };
    } catch (error) {
      console.error("FOVCalculator: Error converting camera dimensions:", error);
      return null;
    }
  },

  /**
   * Calculates a single FOV dimension using atan formula
   * @param {number} sensorDimension - Sensor dimension in mm
   * @param {number} focalLength - Telescope focal length in mm
   * @param {number} focalLengthAdjust - Focal length adjustment factor
   * @returns {number} FOV dimension in degrees
   */
  calculateFOVDimension: function (sensorDimension, focalLength, focalLengthAdjust = 1) {
    try {
      // Validate inputs
      if (
        typeof sensorDimension !== "number" ||
        Number.isNaN(sensorDimension) ||
        sensorDimension <= 0
      ) {
        console.warn("FOVCalculator: Invalid sensor dimension:", sensorDimension);
        return 0;
      }

      if (typeof focalLength !== "number" || Number.isNaN(focalLength) || focalLength <= 0) {
        console.warn("FOVCalculator: Invalid focal length:", focalLength);
        return 0;
      }

      if (
        typeof focalLengthAdjust !== "number" ||
        Number.isNaN(focalLengthAdjust) ||
        focalLengthAdjust <= 0
      ) {
        console.warn("FOVCalculator: Invalid focal length adjustment:", focalLengthAdjust);
        return 0;
      }

      // Validate ranges
      if (focalLength > this.VALIDATION.MAX_FOCAL_LENGTH) {
        console.warn("FOVCalculator: Focal length exceeds maximum:", focalLength);
      }

      if (sensorDimension > this.VALIDATION.MAX_SENSOR_SIZE) {
        console.warn("FOVCalculator: Sensor dimension exceeds maximum:", sensorDimension);
      }

      const effectiveFocalLength = focalLength * focalLengthAdjust;
      const fovRadians = 2 * Math.atan(sensorDimension / (2 * effectiveFocalLength));
      const fovDegrees = fovRadians * this.RAD_TO_DEG;

      // Validate result
      if (fovDegrees > this.VALIDATION.MAX_FOV_DEGREES) {
        console.warn("FOVCalculator: Calculated FOV exceeds maximum degrees:", fovDegrees);
      }

      if (fovDegrees < this.VALIDATION.MIN_FOV_DEGREES) {
        console.warn("FOVCalculator: Calculated FOV below minimum degrees:", fovDegrees);
      }

      return fovDegrees;
    } catch (error) {
      console.error("FOVCalculator: Error calculating FOV dimension:", error);
      return 0;
    }
  },

  /**
   * Validates camera and telescope properties for FOV calculation
   * @param {Object} cameraProps - Camera properties object
   * @param {Object} telescopeProps - Telescope properties object
   * @returns {boolean} True if all required properties are present
   */
  validateCameraTelescopeProperties: (cameraProps, telescopeProps) =>
    !!(
      cameraProps &&
      telescopeProps &&
      cameraProps.sensorWidthInPixels &&
      cameraProps.sensorHeightInPixels &&
      cameraProps.sensorPixelWidth &&
      cameraProps.sensorPixelHeight &&
      telescopeProps.focalLength
    ),

  /**
   * Calculates FOV for telescope + camera combination
   * @param {Object} telescope - Telescope equipment object
   * @param {Object} camera - Camera equipment object
   * @param {number} focalLengthAdjust - Focal length adjustment factor
   * @returns {Object} FOV object with calculated dimensions or error values
   */
  calculateTelescopeCameraFOV: function (telescope, camera, focalLengthAdjust = 1) {
    const cameraProps = camera?.get?.("properties");
    const telescopeProps = telescope?.get?.("properties");

    // Validate required properties
    if (!this.validateCameraTelescopeProperties(cameraProps, telescopeProps)) {
      return { ...this.ERROR_FOV };
    }

    // Convert camera dimensions to millimeters
    const cameraDimensions = this.convertCameraDimensionsToMM(cameraProps);
    if (!cameraDimensions) {
      return { ...this.ERROR_FOV };
    }

    const focalLength = parseFloat(telescopeProps.focalLength);
    const fov = { ...this.DEFAULT_FOV };

    // Calculate main FOV dimensions
    fov.width = this.calculateFOVDimension(
      cameraDimensions.widthMM,
      focalLength,
      focalLengthAdjust,
    );
    fov.height = this.calculateFOVDimension(
      cameraDimensions.heightMM,
      focalLength,
      focalLengthAdjust,
    );

    // Calculate offsets if available and non-zero
    // Offset of 0 is valid (means centered), no calculation needed
    const offsetX = parseFloat(cameraProps.sensorOffsetX);
    if (offsetX && !Number.isNaN(offsetX)) {
      fov.offsetX = this.calculateFOVDimension(offsetX, focalLength, focalLengthAdjust);
    }

    const offsetY = parseFloat(cameraProps.sensorOffsetY);
    if (offsetY && !Number.isNaN(offsetY)) {
      fov.offsetY = this.calculateFOVDimension(offsetY, focalLength, focalLengthAdjust);
    }

    return fov;
  },

  /**
   * Calculates FOV for binoculars
   * @param {Object} binoculars - Binoculars equipment object
   * @returns {Object} FOV object with binoculars field of view
   */
  calculateBinocularsFOV: function (binoculars) {
    try {
      const fov = { ...this.DEFAULT_FOV };

      if (!binoculars || typeof binoculars.get !== "function") {
        console.warn("FOVCalculator: Invalid binoculars object");
        return fov;
      }

      const properties = binoculars.get("properties");
      if (!properties || typeof properties !== "object") {
        console.warn("FOVCalculator: Binoculars missing properties");
        return fov;
      }

      if (!properties.fieldOfView) {
        console.warn("FOVCalculator: Binoculars missing field of view property");
        return fov;
      }

      const fieldOfView = parseFloat(properties.fieldOfView);
      if (Number.isNaN(fieldOfView) || fieldOfView <= 0) {
        console.warn("FOVCalculator: Invalid binoculars field of view:", properties.fieldOfView);
        return fov;
      }

      if (fieldOfView > this.VALIDATION.MAX_FOV_DEGREES) {
        console.warn("FOVCalculator: Binoculars FOV exceeds maximum:", fieldOfView);
      }

      fov.width = fieldOfView;
      return fov;
    } catch (error) {
      console.error("FOVCalculator: Error calculating binoculars FOV:", error);
      return { ...this.DEFAULT_FOV };
    }
  },

  /**
   * Calculates magnification for binoculars
   * @param {Object} binoculars - Binoculars equipment object
   * @returns {number} Magnification value or 0 if not available
   */
  calculateBinocularsMagnification: function (binoculars) {
    try {
      if (!binoculars || typeof binoculars.get !== "function") {
        console.warn("FOVCalculator: Invalid binoculars object for magnification");
        return 0;
      }

      const properties = binoculars.get("properties");
      if (!properties || typeof properties !== "object") {
        console.warn("FOVCalculator: Binoculars missing properties for magnification");
        return 0;
      }

      if (!properties.magnification) {
        console.warn("FOVCalculator: Binoculars missing magnification property");
        return 0;
      }

      const magnification = parseFloat(properties.magnification);
      if (Number.isNaN(magnification) || magnification <= 0) {
        console.warn("FOVCalculator: Invalid binoculars magnification:", properties.magnification);
        return 0;
      }

      if (magnification > this.VALIDATION.MAX_MAGNIFICATION) {
        console.warn("FOVCalculator: Binoculars magnification exceeds maximum:", magnification);
      }

      return magnification;
    } catch (error) {
      console.error("FOVCalculator: Error calculating binoculars magnification:", error);
      return 0;
    }
  },

  /**
   * Calculates FOV for custom field of view settings
   * @param {number} customFOV - Custom FOV width in degrees
   * @param {number} customFOVHeight - Custom FOV height in degrees (optional)
   * @returns {Object} FOV object with custom dimensions
   */
  calculateCustomFOV: function (customFOV, customFOVHeight = 0) {
    try {
      const fov = { ...this.DEFAULT_FOV };

      // Validate and set width
      if (customFOV !== undefined && customFOV !== null) {
        const widthValue = parseFloat(customFOV);
        if (Number.isNaN(widthValue)) {
          console.warn("FOVCalculator: Invalid custom FOV width value:", customFOV);
          fov.width = 0;
        } else if (widthValue < 0) {
          console.warn("FOVCalculator: Custom FOV width cannot be negative:", widthValue);
          fov.width = 0;
        } else if (widthValue > this.VALIDATION.MAX_FOV_DEGREES) {
          console.warn("FOVCalculator: Custom FOV width exceeds maximum:", widthValue);
          fov.width = this.VALIDATION.MAX_FOV_DEGREES;
        } else {
          fov.width = widthValue;
        }
      }

      // Validate and set height
      if (customFOVHeight !== undefined && customFOVHeight !== null) {
        const heightValue = parseFloat(customFOVHeight);
        if (Number.isNaN(heightValue)) {
          console.warn("FOVCalculator: Invalid custom FOV height value:", customFOVHeight);
          fov.height = 0;
        } else if (heightValue < 0) {
          console.warn("FOVCalculator: Custom FOV height cannot be negative:", heightValue);
          fov.height = 0;
        } else if (heightValue > this.VALIDATION.MAX_FOV_DEGREES) {
          console.warn("FOVCalculator: Custom FOV height exceeds maximum:", heightValue);
          fov.height = this.VALIDATION.MAX_FOV_DEGREES;
        } else {
          fov.height = heightValue;
        }
      }

      return fov;
    } catch (error) {
      console.error("FOVCalculator: Error calculating custom FOV:", error);
      return { ...this.DEFAULT_FOV };
    }
  },

  /**
   * Main FOV calculation method that determines the appropriate calculation based on equipment
   * @param {Object} equipment - Object containing all equipment references
   * @param {Object} customSettings - Object containing custom FOV settings
   * @returns {Object} Calculated FOV object
   */
  calculateFOV: function (equipment = {}, customSettings = {}) {
    const { telescope, eyepiece, camera, barlowReducer, binoculars } = equipment;

    const { useCustomFOV = false, customFOV = 0, customFOVHeight = 0 } = customSettings;

    // Custom FOV takes precedence if enabled
    if (useCustomFOV) {
      return this.calculateCustomFOV(customFOV, customFOVHeight);
    }

    // Calculate focal length adjustment if barlow/reducer is present
    const focalLengthAdjust = this.calculateFocalLengthAdjustment(barlowReducer);

    // Telescope combinations
    if (telescope) {
      // Telescope + eyepiece
      if (eyepiece) {
        return this.calculateTelescopeEyepieceFOV(telescope, eyepiece, focalLengthAdjust);
      }

      // Telescope + camera
      if (camera) {
        return this.calculateTelescopeCameraFOV(telescope, camera, focalLengthAdjust);
      }
    }

    // Binoculars (standalone)
    if (binoculars) {
      return this.calculateBinocularsFOV(binoculars);
    }

    // Fallback to custom FOV if no equipment is configured
    return this.calculateCustomFOV(customFOV, customFOVHeight);
  },

  /**
   * Calculates total magnification based on equipment configuration
   * @param {Object} equipment - Object containing all equipment references
   * @returns {number|string} Magnification value or error message
   */
  calculateMagnificationTotal: function (equipment = {}) {
    const { telescope, eyepiece, barlowReducer, binoculars } = equipment;

    // Binoculars magnification
    if (binoculars) {
      return this.calculateBinocularsMagnification(binoculars);
    }

    // Telescope + eyepiece magnification
    if (telescope && eyepiece) {
      const focalLengthAdjust = this.calculateFocalLengthAdjustment(barlowReducer);
      return this.calculateMagnification(telescope, eyepiece, focalLengthAdjust);
    }

    return "no magnification found";
  },

  /**
   * Utility method to format FOV for display
   * @param {Object} fov - FOV object from calculation
   * @param {number} decimalPlaces - Number of decimal places for formatting
   * @returns {string} Formatted FOV string
   */
  formatFOVForDisplay: (fov, decimalPlaces = 2) => {
    if (!fov || fov.width <= 0) {
      return "Invalid FOV";
    }

    if (fov.height && fov.height > 0) {
      return `${fov.width.toFixed(decimalPlaces)}° x ${fov.height.toFixed(decimalPlaces)}°`;
    }

    return `${fov.width.toFixed(decimalPlaces)}°`;
  },

  /**
   * Checks if FOV calculation resulted in an error state
   * @param {Object} fov - FOV object to check
   * @returns {boolean} True if FOV indicates an error
   */
  isErrorFOV: (fov) =>
    !!(fov && (fov.width === -1 || fov.height === -1 || fov.offsetX === -1 || fov.offsetY === -1)),

  /**
   * Validates that FOV values are reasonable (positive and within expected ranges)
   * @param {Object} fov - FOV object to validate
   * @param {number} maxFOV - Maximum reasonable FOV in degrees (default: 360)
   * @returns {boolean} True if FOV values are valid
   */
  isValidFOV: (fov, maxFOV = 360) => {
    if (!fov || typeof fov !== "object") {
      return false;
    }

    // Check width (required)
    if (typeof fov.width !== "number" || fov.width < 0 || fov.width > maxFOV) {
      return false;
    }

    // Check height (optional, but if present should be valid)
    if (
      fov.height !== undefined &&
      (typeof fov.height !== "number" || fov.height < 0 || fov.height > maxFOV)
    ) {
      return false;
    }

    // Check offsets (optional, but if present should be reasonable)
    if (
      fov.offsetX !== undefined &&
      (typeof fov.offsetX !== "number" || Math.abs(fov.offsetX) > maxFOV)
    ) {
      return false;
    }

    if (
      fov.offsetY !== undefined &&
      (typeof fov.offsetY !== "number" || Math.abs(fov.offsetY) > maxFOV)
    ) {
      return false;
    }

    return true;
  },
};

export default FOVCalculator;
